import { motion } from 'framer-motion';
import { Leaf, Recycle, Droplets, TreePine, Heart, Trash2 } from 'lucide-react';

export default function Sustainability() {
  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid="sustainability-page">
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="badge mb-4">
              Event Details
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ff-brown">
              <span className="text-ff-brown">Sustainability</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <Leaf className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">Leave No Trace</h2>
              </div>
              <p className="text-ff-brown/70 text-lg">
                At Frequency Forest, we are deeply committed to protecting the natural environment 
                that hosts our celebration. We operate on strict <strong className="text-ff-brown">Leave No Trace</strong> principles, 
                and we ask all attendees to join us in leaving the land in better condition than 
                when we arrived.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Trash2 className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Pack It In, Pack It Out</h3>
                <p className="text-ff-brown/70 text-sm">
                  Whatever you bring to the festival, you take home with you. This includes 
                  all rubbish, camping gear, and decorations. Leave your campsite cleaner than 
                  you found it.
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Recycle className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Waste Stations</h3>
                <p className="text-ff-brown/70 text-sm">
                  We provide recycling and waste stations throughout the venue. Please sort 
                  your waste correctly: recyclables, compost/organics, and landfill.
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Droplets className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Water Conservation</h3>
                <p className="text-ff-brown/70 text-sm">
                  Water is precious in the bush. Free water refill stations are available – 
                  bring your own reusable bottle. We encourage quick showers (if available).
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <TreePine className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Respect the Land</h3>
                <p className="text-ff-brown/70 text-sm">
                  Do not damage trees, plants, or wildlife. Stay on designated paths. 
                  No fires or open flames. Use designated toilet facilities only.
                </p>
              </div>
            </div>

            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8 border-l-4 border-ff-secondary">
              <h3 className="font-display text-xl text-ff-brown mb-4">Our Pledge</h3>
              <ul className="text-ff-brown/70 space-y-2">
                <li className="flex items-start gap-3">
                  <Heart className="w-4 h-4 text-ff-brown flex-shrink-0 mt-1" />
                  Offsetting carbon emissions from the event
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="w-4 h-4 text-ff-brown flex-shrink-0 mt-1" />
                  Using renewable energy where possible
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="w-4 h-4 text-ff-brown flex-shrink-0 mt-1" />
                  Minimizing single-use plastics across vendors
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="w-4 h-4 text-ff-brown flex-shrink-0 mt-1" />
                  Partnering with local environmental groups
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="w-4 h-4 text-ff-brown flex-shrink-0 mt-1" />
                  Post-event clean-up and land restoration
                </li>
              </ul>
            </div>

            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8">
              <h3 className="font-display text-xl text-ff-brown mb-4">How You Can Help</h3>
              <div className="grid md:grid-cols-2 gap-4 text-ff-brown/70 text-sm">
                <ul className="space-y-2">
                  <li>• Bring reusable cups, plates, and utensils</li>
                  <li>• Carpool to reduce emissions</li>
                  <li>• Choose eco-friendly camping gear</li>
                  <li>• Pick up any litter you see</li>
                </ul>
                <ul className="space-y-2">
                  <li>• Avoid single-use plastics</li>
                  <li>• Use reef-safe sunscreen</li>
                  <li>• Respect wildlife and plants</li>
                  <li>• Spread the word about Leave No Trace</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
